import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { api } from "../lib/api";
import { todayBucharestIso } from "../lib/intervals";
import { mobileNavigationFor } from "../lib/navigation";
import { formatMw, formatPrice, signClass } from "../lib/utils";
import { useAuth, type AuthUser } from "../stores/auth";
import { useMobileUiV1State } from "../hooks/use-media-query";
import type {
  TradesRequest,
  TradesResponse,
} from "../features/trading/trades/contracts";
import {
  buildTradesRequestPath,
  tradesQueryKeys,
} from "../features/trading/trades/queries";
import {
  deriveIdmSummary,
  fxSummaryText,
} from "../features/trading/trades/selectors";
import type { RecognitionResponse } from "../features/eex/pnl/contracts";
import {
  buildRecognitionMonthsRequestPath,
  recognitionQueryKeys,
} from "../features/eex/pnl/queries";
import { selectRecognitionTableFacts, selectLatestApprovedMonth } from "../features/eex/pnl/selectors";

export const Route = createFileRoute("/")({
  component: Home,
});

function Home() {
  const user = useAuth((state) => state.user);
  const { isMobileViewport } = useMobileUiV1State();
  const mobileDestinations = user
    ? mobileNavigationFor(user).flatMap((section) =>
        section.items.map((item) => ({ ...item, section: section.mobileLabel }))
      )
    : [];

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:px-6 sm:py-10">
      <div className="space-y-2">
        <h1 className="text-xl font-semibold tracking-[0.14em] sm:text-2xl">
          CASCENA
        </h1>
        <p className="text-sm text-muted sm:hidden">
          Monitor the business and open a read-only investigation workflow.
        </p>
        <p className="hidden text-muted sm:block">
          Power trading workspace for Romanian DAM / IDM / Imbalance markets.
        </p>
      </div>
      {user && isMobileViewport && <MobileHeadlineSummary user={user} />}
      <nav
        aria-label="Mobile monitoring workflows"
        className="grid grid-cols-1 gap-3 sm:hidden"
      >
        {mobileDestinations.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="block min-h-11 rounded-md border border-border/60 p-4 transition-colors hover:border-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <div className="mb-1 font-medium">{item.label.replace(" ▸", "")}</div>
            <div className="text-xs text-muted">{item.section} · read-only monitoring</div>
          </Link>
        ))}
        {mobileDestinations.length === 0 && (
          <p className="rounded-md border border-border/60 p-4 text-sm text-muted">
            No mobile monitoring workflows are enabled for this account.
          </p>
        )}
      </nav>
      <nav aria-label="Trading workflows" className="hidden grid-cols-2 gap-4 sm:grid">
        <HomeCard
          to="/grid"
          title="Position & P&L"
          body="96-QH grid: DA base + IDM buys/sells (MWh + VWAP) + net MW + P&L."
        />
        <HomeCard
          to="/trades"
          title="Trades"
          body="Browse BRMID and BRMDA fills with filters and per-interval rollup."
        />
        <HomeCard
          to="/reports"
          title="Reports"
          body="Daily trading preview and XLSX export with historical EUR/RON conversion."
        />
        <HomeCard
          to="/ingest"
          title="Ingest"
          body="Upload daily DAM schedule + trade blotter files."
        />
      </nav>
    </div>
  );
}

function MobileHeadlineSummary({ user }: { user: AuthUser }) {
  const deliveryDate = todayBucharestIso();
  const physicalRequest: TradesRequest = {
    deliveryDateFrom: deliveryDate,
    deliveryDateTo: deliveryDate,
    market: "",
    portfolioId: "",
    side: "",
    productCode: "",
    limit: 1,
    offset: 0,
  };
  const physical = useQuery({
    queryKey: tradesQueryKeys.list(physicalRequest),
    queryFn: () => api<TradesResponse>(buildTradesRequestPath(physicalRequest)),
    enabled: user.tradingEnabled,
    staleTime: 30_000,
  });
  const recognition = useQuery({
    queryKey: recognitionQueryKeys.months(),
    queryFn: () => api<RecognitionResponse>(buildRecognitionMonthsRequestPath()),
    enabled: user.eexEnabled,
    staleTime: 30_000,
  });
  const combined = physical.data?.totals.combined ?? null;
  const physicalNetMwh = physical.data
    ? deriveIdmSummary(
        physical.data.totals.combinedBreakdown,
        physical.data.totals.combined?.rowCount ?? 0
      ).netMwh
    : undefined;
  const newestPhysicalSource = physical.data?.trades[0] ?? null;
  const recognitionFacts = recognition.data
    ? selectRecognitionTableFacts(recognition.data)
    : null;
  const latestRecognitionMonth = recognition.data ? selectLatestApprovedMonth(recognition.data) : null;

  if (!user.tradingEnabled && !user.eexEnabled) return null;

  return (
    <section aria-labelledby="mobile-headline-heading" className="space-y-2 sm:hidden">
      <h2 id="mobile-headline-heading" className="text-xs font-semibold uppercase tracking-wide text-muted">
        Headline
      </h2>
      <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2">
        {user.tradingEnabled && (
          <HeadlineCard to="/trades" title={`Physical · ${deliveryDate}`} busy={physical.isLoading} error={physical.error}>
            <HeadlineMetric
              label="DA + ID net"
              value={`${signed(formatMw(physicalNetMwh), physicalNetMwh)} MWh`}
              amount={physicalNetMwh}
            />
            <HeadlineMetric
              label="Closed P&L"
              value={`${signed(formatPrice(combined?.pnl), combined?.pnl)} EUR`}
              amount={combined?.pnl}
            />
            <p className="text-xs text-muted">
              {physical.data
                ? fxSummaryText(physical.data.totals.fxSummary, "converted via") ??
                  "FX not required"
                : "FX not required"}
            </p>
            <p className="break-all text-xs text-muted">
              {newestPhysicalSource
                ? `Latest ${newestPhysicalSource.market.toLowerCase()} ${newestPhysicalSource.source} record · ${newestPhysicalSource.ingestedAt}`
                : "No trade or schedule source observation"}
            </p>
          </HeadlineCard>
        )}
        {user.eexEnabled && (
          <HeadlineCard to="/eex/pnl" title="EEX recognition" busy={recognition.isLoading} error={recognition.error}>
            <HeadlineMetric
              label={latestRecognitionMonth ? `${latestRecognitionMonth.month} approved` : "Approved P&L"}
              value={`${signed(formatPrice(latestRecognitionMonth?.bookedEUR), latestRecognitionMonth?.bookedEUR)} EUR`}
              amount={latestRecognitionMonth?.bookedEUR}
            />
            <HeadlineMetric
              label="Stale contracts"
              value={recognitionFacts ? String(recognitionFacts.staleContracts.length) : "—"}
              amount={recognitionFacts?.staleContracts.length}
              negativeWhenPositive
            />
            <p className="text-xs text-muted">As of {recognition.data?.asOf ?? "—"}</p>
          </HeadlineCard>
        )}
      </div>
    </section>
  );
}

function HeadlineCard({
  to,
  title,
  busy,
  error,
  children,
}: {
  to: "/trades" | "/eex/pnl";
  title: string;
  busy: boolean;
  error: Error | null;
  children: ReactNode;
}) {
  return (
    <Link to={to} className="min-w-0 rounded-md border border-border/60 p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
      <h3 className="font-medium">{title}</h3>
      {busy ? <p className="mt-2 text-sm text-muted">Loading…</p> : error ? <p className="mt-2 text-sm text-negRed">Unavailable</p> : <div className="mt-2 space-y-1">{children}</div>}
    </Link>
  );
}

function HeadlineMetric({
  label,
  value,
  amount,
  negativeWhenPositive = false,
}: {
  label: string;
  value: string;
  amount: number | null | undefined;
  negativeWhenPositive?: boolean;
}) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-3 text-sm">
      <span className="text-muted">{label}</span>
      <span className={negativeWhenPositive && amount && amount > 0 ? "text-negRed" : signClass(amount)}>{value}</span>
    </div>
  );
}

function signed(value: string, amount: number | null | undefined): string {
  return amount != null && amount > 0 ? `+${value}` : value;
}

function HomeCard({ to, title, body }: { to: string; title: string; body: string }) {
  return (
    <Link
      to={to}
      className="block rounded-md border border-border/60 p-4 transition-colors hover:border-accent/60"
    >
      <div className="mb-1 font-medium">{title}</div>
      <div className="text-xs text-muted">{body}</div>
    </Link>
  );
}
